import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import InstructorPage from './InstructorPage';
import ImageModal from './ImageModal';
import './InstructorDashboard.css';

const API = process.env.REACT_APP_API_URL;

function formatIST(dateString) {
  if (!dateString) return '';
  const normalized = dateString.endsWith('Z') || dateString.includes('+') ? dateString : dateString + 'Z';
  const date = new Date(normalized);
  const istMillis = date.getTime() + (5 * 60 + 30) * 60 * 1000;
  const ist = new Date(istMillis);
  const day = ist.getUTCDate().toString().padStart(2, '0');
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const month = months[ist.getUTCMonth()];
  const year = ist.getUTCFullYear();
  let hours = ist.getUTCHours();
  const minutes = ist.getUTCMinutes().toString().padStart(2, '0');
  const ampm = hours >= 12 ? 'pm' : 'am';
  hours = hours % 12 || 12;
  return `${day} ${month} ${year}, ${hours}:${minutes} ${ampm}`;
}

// 0% correct = red, 100% = green — used for the topic × difficulty heat grid
function pctColor(pct) {
  const hue = Math.round((Math.max(0, Math.min(100, pct)) / 100) * 120);
  return `hsl(${hue}, 55%, 45%)`;
}

export default function InstructorDashboard({ onNavigate, onStudentView }) {
  const { token, user, logout } = useAuth();
  const [tab, setTab] = useState('analytics');
  const [overview, setOverview] = useState(null);
  const [insights, setInsights] = useState(null);
  const [students, setStudents] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [studentHistory, setStudentHistory] = useState(null);
  const [sessionDetail, setSessionDetail] = useState(null);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [topics, setTopics] = useState([]);
  const [questionTopics, setQuestionTopics] = useState({});
  const [editForm, setEditForm] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [enlargedImage, setEnlargedImage] = useState(null);
  const [questionSearch, setQuestionSearch] = useState('');
  const [questionPage, setQuestionPage] = useState(1);
  const [questionTotalPages, setQuestionTotalPages] = useState(1);
  const [questionTotal, setQuestionTotal] = useState(0);
  const [filterTopic, setFilterTopic] = useState('');
  const [filterDifficulty, setFilterDifficulty] = useState('');
  const [filterTime, setFilterTime] = useState('');
  const [questionsLoading, setQuestionsLoading] = useState(false);
  const QUESTIONS_PER_PAGE = 10;

  useEffect(() => {
    if (tab === 'analytics') fetchOverview();
    if (tab === 'insights') fetchInsights();
    if (tab === 'questions') fetchTopicsForFilter();
  }, [tab]);

  // Server-side questions: refetch when the tab is active and whenever the
  // search / filters / page change (search is debounced).
  useEffect(() => {
    if (tab !== 'questions') return undefined;
    const t = setTimeout(() => { fetchQuestions(); }, 300);
    return () => clearTimeout(t);
  }, [tab, questionPage, questionSearch, filterTopic, filterDifficulty, filterTime]);

  function formatTime(seconds) {
    if (!seconds && seconds !== 0) return '—';
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return m > 0 ? `${m}m ${s}s` : `${s}s`;
  }

  async function fetchOverview() {
    setLoading(true);
    setError(null);
    try {
      const [overviewRes, studentsRes] = await Promise.all([
        fetch(`${API}/api/analytics/overview`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/api/analytics/students`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      const overviewData = await overviewRes.json();
      const studentsData = await studentsRes.json();
      if (!overviewRes.ok) throw new Error(overviewData.error || 'Failed to load analytics');
      setOverview(overviewData);
      setStudents(studentsData.students || []);
    } catch (err) {
      setError(err.message);
      setOverview(null);
      setStudents([]);
    } finally {
      setLoading(false);
    }
  }

  async function fetchInsights() {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API}/api/analytics/topics-difficulty`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load insights');
      setInsights(data);
    } catch (err) {
      setError(err.message);
      setInsights(null);
    } finally {
      setLoading(false);
    }
  }

  async function fetchQuestions() {
    setQuestionsLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(questionPage),
        limit: String(QUESTIONS_PER_PAGE),
      });
      if (questionSearch.trim()) params.set('search', questionSearch.trim());
      if (filterTopic) params.set('topic', filterTopic);
      if (filterDifficulty) params.set('difficulty', filterDifficulty);
      if (filterTime) params.set('time', filterTime);
      const res = await fetch(`${API}/api/questions?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setQuestions(data.questions);
      setQuestionTotalPages(data.pagination.totalPages);
      setQuestionTotal(data.pagination.total);
    } catch (err) {
      setError(err.message);
    } finally {
      setQuestionsLoading(false);
    }
  }

  async function fetchTopicsForFilter() {
    try {
      const res = await fetch(`${API}/api/topics`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      if (res.ok) setTopics(data.topics || []);
    } catch (err) {
      // ignore — the topic filter just won't populate
    }
  }

  async function fetchStudentHistory(studentId) {
    try {
      const res = await fetch(`${API}/api/analytics/students/${studentId}/history`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setStudentHistory(data);
      setSessionDetail(null);
    } catch (err) {
      alert(err.message);
    }
  }

  async function fetchSessionDetail(sessionId) {
    try {
      const res = await fetch(`${API}/api/session/${sessionId}/results`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setSessionDetail(data);
    } catch (err) {
      alert(err.message);
    }
  }

  async function saveEdit(questionId) {
    try {
      const res = await fetch(`${API}/api/questions/${questionId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(editForm),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setQuestions((prev) =>
        prev.map((q) => (q.id === questionId ? data.question : q))
      );
      setEditingQuestion(null);
      setEditForm({});
    } catch (err) {
      alert(err.message);
    }
  }

  async function toggleEnabled(questionId, enabled) {
    try {
      const res = await fetch(`${API}/api/questions/${questionId}/enabled`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ enabled }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setQuestions((prev) => prev.map((q) => (q.id === questionId ? data.question : q)));
    } catch (err) {
      alert(err.message);
    }
  }

  async function deleteQuestion(questionId) {
    // First fetch how many student responses will be affected
    let responseCount = 0;
    try {
      const countRes = await fetch(`${API}/api/questions/${questionId}/response-count`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const countData = await countRes.json();
      responseCount = countData.count || 0;
    } catch {
      // If count fails, proceed with generic warning
    }

    const message = responseCount > 0
      ? `Delete this question? This will also permanently delete ${responseCount} student response${responseCount !== 1 ? 's' : ''} to it. This cannot be undone.`
      : 'Delete this question? This cannot be undone.';

    if (!window.confirm(message)) return;
    try {
      const res = await fetch(`${API}/api/questions/${questionId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to delete');
      setQuestions((prev) => prev.filter((q) => q.id !== questionId));
    } catch (err) {
      alert(err.message);
    }
  }

  async function saveTopics(questionId, topicIds) {
    try {
      await fetch(`${API}/api/topics/question/${questionId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ topicIds }),
      });
      setQuestions((prev) => prev.map((q) => {
        if (q.id !== questionId) return q;
        const selectedTopics = topics.filter((t) => topicIds.includes(t.id));
        return { ...q, topics: selectedTopics };
      }));
    } catch (err) {
      alert(err.message);
    }
  }

  function exportStudentsCSV() {
    if (!students.length) return;
    const rows = [
      ['Username', 'Email', 'Total Attempts', 'Best Score'],
      ...students.map((s) => [s.username, s.email, s.total_attempts, s.best_score !== null ? `${s.best_score}/10` : '—']),
    ];
    const csv = rows.map((r) => r.map((c) => `"${c}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'students-export.csv';
    a.click();
    URL.revokeObjectURL(url);
  }

    function startEdit(question) {
    setEditingQuestion(question.id);
    setEditForm({
      option_a: question.option_a,
      option_b: question.option_b,
      option_c: question.option_c,
      option_d: question.option_d,
      option_e: question.option_e,
      correct_option: question.correct_option,
      video_url: question.video_url || '',
      time_limit_seconds: question.time_limit_seconds || '',
      difficulty: question.difficulty || '',
      question_text: question.question_text || '',
    });
  }

  return (
    <div className="idash-wrapper">
      {enlargedImage && (
        <ImageModal src={enlargedImage} alt="Question diagram" onClose={() => setEnlargedImage(null)} />
      )}
      <div className="idash-card">
        {/* Header */}
        <div className="idash-header">
          <div>
            <h2 className="idash-title">Instructor Panel</h2>
            <p className="idash-subtitle">Welcome, {user.username}</p>
          </div>
          <div className="idash-header-actions">
            {user.role === 'admin' && (
              <button className="nav-action-btn" onClick={() => onNavigate('admin')}>
                Admin Dashboard
              </button>
            )}
            <button className="nav-action-btn" onClick={() => onNavigate('provision')}>
              + Add Users
            </button>
            <button className="nav-action-btn student-view-btn" onClick={onStudentView}>
              👁 Student View
            </button>
            <button className="nav-action-btn logout" onClick={logout}>Sign Out</button>
          </div>
        </div>

        {/* Tabs */}
        <div className="idash-tabs">
          {['analytics', 'questions', 'insights', 'upload'].map((t) => (
            <button
              key={t}
              className={`idash-tab ${tab === t ? 'active' : ''}`}
              onClick={() => {
                setTab(t);
                setStudentHistory(null);
                setSessionDetail(null);
              }}
            >
              {t === 'insights' ? 'Topic Insights' : t.charAt(0).toUpperCase() + t.slice(1)}
            </button>
          ))}
        </div>

        {loading && <p className="idash-loading">Loading...</p>}
        {error && <p className="idash-error">{error}</p>}

        {/* Analytics Tab */}
        {tab === 'analytics' && !loading && overview && (
          <div className="analytics-content">
            {/* Stats row */}
            <div className="stats-row">
              <div className="stat-box">
                <span className="stat-value">{overview.total_attempts}</span>
                <span className="stat-label">Total Attempts</span>
              </div>
              <div className="stat-box">
                <span className="stat-value">{overview.unique_students}</span>
                <span className="stat-label">Unique Students</span>
              </div>
              <div className="stat-box">
                <span className="stat-value">{overview.avg_score}/10</span>
                <span className="stat-label">Avg Score</span>
              </div>
            </div>

            {/* Session detail drill-down */}
            {sessionDetail ? (
              <div className="session-detail">
                <button className="back-link" onClick={() => setSessionDetail(null)}>
                  ← Back to attempts
                </button>
                <div className="session-detail-header">
                  <h3 className="history-title">Attempt Detail</h3>
                  <div className="session-summary">
                    <span className="session-score">Score: {sessionDetail.score}/{sessionDetail.total}</span>
                    <span className="session-time">Total time: {formatTime(sessionDetail.total_time)}</span>
                  </div>
                </div>
                <div className="session-questions">
                  {sessionDetail.results.map((r, i) => (
                    <div key={r.id} className={`session-q-item ${r.is_correct ? 'correct' : 'wrong'}`}>
                      <img src={r.image_filename} alt={`Q${i+1}`} className="session-q-img clickable-img" onClick={() => setEnlargedImage(r.image_filename)} />
                      <div className="session-q-info">
                        <div className="session-q-row">
                          <span className="session-q-num">Question {i + 1}</span>
                          <span className="session-q-time">⏱ {formatTime(r.time_taken_seconds)}</span>
                        </div>
                        <span className={`answer-tag ${r.is_correct ? 'tag-correct' : 'tag-wrong'}`}>
                          Answered: {r.chosen_option ? `${r.chosen_option} — ${r.options[r.chosen_option]}` : 'Not answered'}
                        </span>
                        {!r.is_correct && (
                          <span className="answer-tag tag-correct">
                            Correct: {r.correct_option} — {r.options[r.correct_option]}
                          </span>
                        )}
                      </div>
                      <span className={`breakdown-icon ${r.is_correct ? 'icon-correct' : 'icon-wrong'}`}>
                        {r.is_correct ? '✓' : '✗'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ) : studentHistory ? (
              <div className="student-history">
                <button className="back-link" onClick={() => setStudentHistory(null)}>
                  ← Back to students
                </button>
                <h3 className="history-title">{studentHistory.student.username}'s Attempts</h3>
                {studentHistory.attempts.length === 0 ? (
                  <p className="empty-msg">No attempts yet.</p>
                ) : (
                  <table className="history-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Score</th>
                        <th>Total Time</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {studentHistory.attempts.map((a) => (
                        <tr key={a.session_id}>
                          <td>{formatIST(a.created_at)}</td>
                          <td className="score-cell">{a.correct_count}/{a.questions_answered}</td>
                          <td className="time-cell">{formatTime(a.total_time)}</td>
                          <td>
                            <button
                              className="view-history-btn"
                              onClick={() => fetchSessionDetail(a.session_id)}
                            >
                              Full Detail
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            ) : (
              <>
                {/* Question difficulty */}
                <div className="section">
                  <h3 className="section-title">Question Difficulty</h3>
                  <div className="difficulty-list">
                    {(overview.question_difficulty || []).map((q, i) => (
                      <div key={q.id} className="difficulty-item">
                        <img src={q.image_filename} alt={`Q${i + 1}`} className="diff-img clickable-img" onClick={() => setEnlargedImage(q.image_filename)} />
                        <div className="diff-bar-wrapper">
                          <div className="diff-bar" style={{ width: `${q.wrong_percentage || 0}%` }} />
                        </div>
                        <span className="diff-pct">{q.wrong_percentage || 0}% wrong</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Students list */}
                <div className="section">
                  <div className="section-header">
                    <h3 className="section-title">Students</h3>
                    <button className="export-students-btn" onClick={exportStudentsCSV}>⬇ Export CSV</button>
                  </div>
                  {students.length === 0 ? (
                    <p className="empty-msg">No students have registered yet.</p>
                  ) : (
                    <table className="students-table">
                      <thead>
                        <tr>
                          <th>Username</th>
                          <th>Attempts</th>
                          <th>Best Score</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {students.map((s) => (
                          <tr key={s.id}>
                            <td className="td-username">{s.username}</td>
                            <td>{s.total_attempts}</td>
                            <td>{s.best_score !== null ? `${s.best_score}/10` : '—'}</td>
                            <td>
                              <button
                                className="view-history-btn"
                                onClick={() => fetchStudentHistory(s.id)}
                              >
                                View History
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {/* Topic Insights Tab */}
        {tab === 'insights' && !loading && insights && (
          <div className="analytics-content">
            {/* 1. Average score per difficulty level (all quizzes) */}
            <div className="section">
              <div className="section-title">Average score by difficulty</div>
              <p className="section-note">Across all quizzes — random and topic.</p>
              {(!insights.per_difficulty || insights.per_difficulty.length === 0) ? (
                <p className="empty-msg">No graded answers yet.</p>
              ) : (
                <div className="difficulty-list">
                  {insights.per_difficulty.map((d) => (
                    <div key={d.difficulty} className="insight-bar-row">
                      <span className="insight-bar-label">Level {d.difficulty}</span>
                      <div className="diff-bar-wrapper">
                        <div className="insight-bar-fill" style={{ width: `${Number(d.correct_pct) || 0}%` }} />
                      </div>
                      <span className="diff-pct">{d.correct_pct == null ? '—' : `${d.correct_pct}%`}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* 2. Average score per topic (topic quizzes only) */}
            <div className="section">
              <div className="section-title">Average score by topic</div>
              <p className="section-note">Topic quizzes only — answers from random quizzes are not counted here.</p>
              {(!insights.per_topic || insights.per_topic.length === 0) ? (
                <p className="empty-msg">No topic-quiz attempts yet.</p>
              ) : (
                <div className="difficulty-list">
                  {insights.per_topic.map((t) => (
                    <div key={t.id} className="insight-bar-row">
                      <span className="insight-bar-label">{t.name}</span>
                      <div className="diff-bar-wrapper">
                        <div className="insight-bar-fill" style={{ width: `${Number(t.avg_pct) || 0}%` }} />
                      </div>
                      <span className="diff-pct">
                        {t.avg_pct == null ? '—' : `${t.avg_pct}%`} · {t.attempts} attempt{Number(t.attempts) === 1 ? '' : 's'}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* 3. Topic × difficulty grid (all quizzes) */}
            <div className="section">
              <div className="section-title">Topic × difficulty grid</div>
              <p className="section-note">% correct per topic at each difficulty (1–10), across all quizzes. Blank = no answers yet.</p>
              {(() => {
                const g = insights.grid || [];
                if (g.length === 0) return <p className="empty-msg">No graded answers on topic questions yet.</p>;
                const topicsMap = {};
                const order = [];
                g.forEach((r) => {
                  if (!topicsMap[r.topic_id]) {
                    topicsMap[r.topic_id] = { name: r.topic_name, cells: {} };
                    order.push(r.topic_id);
                  }
                  topicsMap[r.topic_id].cells[r.difficulty] = r.correct_pct;
                });
                const levels = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
                return (
                  <div className="grid-scroll">
                    <table className="heat-table">
                      <thead>
                        <tr>
                          <th className="heat-corner">Topic</th>
                          {levels.map((l) => <th key={l}>{l}</th>)}
                        </tr>
                      </thead>
                      <tbody>
                        {order.map((tid) => {
                          const t = topicsMap[tid];
                          return (
                            <tr key={tid}>
                              <td className="heat-topic">{t.name}</td>
                              {levels.map((l) => {
                                const pct = t.cells[l];
                                return (
                                  <td
                                    key={l}
                                    className="heat-cell"
                                    style={pct == null ? undefined : { background: pctColor(Number(pct)), color: '#fff' }}
                                  >
                                    {pct == null ? '' : pct}
                                  </td>
                                );
                              })}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                );
              })()}
            </div>

            {/* 4. Most-missed question per difficulty (all quizzes) */}
            <div className="section">
              <div className="section-title">Most-missed question per level</div>
              <p className="section-note">The question with the highest wrong-answer rate at each difficulty.</p>
              {(!insights.most_missed || insights.most_missed.length === 0) ? (
                <p className="empty-msg">No graded answers yet.</p>
              ) : (
                <div className="missed-list">
                  {insights.most_missed.map((m) => (
                    <div key={m.id} className="missed-item">
                      <span className="missed-level">L{m.difficulty}</span>
                      <img
                        src={m.image_filename}
                        alt={`Level ${m.difficulty} question`}
                        className="missed-img clickable-img"
                        onClick={() => setEnlargedImage(m.image_filename)}
                      />
                      <div className="missed-meta">
                        <span className="missed-pct">{m.wrong_pct == null ? '—' : `${m.wrong_pct}% missed`}</span>
                        <span className="missed-count">{m.total_answers} answer{Number(m.total_answers) === 1 ? '' : 's'}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Questions Tab */}
        {tab === 'questions' && (
          <div className="questions-content">
            {questionsLoading ? (
              <p className="empty-msg">Loading...</p>
            ) : (
              <>
                <div className="questions-toolbar">
                  <input
                    className="question-search"
                    value={questionSearch}
                    onChange={(e) => { setQuestionSearch(e.target.value); setQuestionPage(1); }}
                    placeholder="Search by option text or topic..."
                  />
                  <select className="question-filter" value={filterTopic} onChange={(e) => { setFilterTopic(e.target.value); setQuestionPage(1); }}>
                    <option value="">All topics</option>
                    {topics.map((t) => (<option key={t.id} value={t.id}>{t.name}</option>))}
                  </select>
                  <select className="question-filter" value={filterDifficulty} onChange={(e) => { setFilterDifficulty(e.target.value); setQuestionPage(1); }}>
                    <option value="">All difficulties</option>
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((d) => (<option key={d} value={d}>Difficulty {d}</option>))}
                  </select>
                  <select className="question-filter" value={filterTime} onChange={(e) => { setFilterTime(e.target.value); setQuestionPage(1); }}>
                    <option value="">Any time limit</option>
                    <option value="has">Has time limit</option>
                    <option value="none">No time limit</option>
                  </select>
                  {(filterTopic || filterDifficulty || filterTime || questionSearch) && (
                    <button className="clear-filters-btn" onClick={() => { setFilterTopic(''); setFilterDifficulty(''); setFilterTime(''); setQuestionSearch(''); setQuestionPage(1); }}>Clear</button>
                  )}
                  <span className="questions-count">
                    {questionTotal} question{questionTotal !== 1 ? 's' : ''}
                  </span>
                </div>

                <div className="questions-list">
                  {questions.map((q, i) => {
                    return (
                  <div key={q.id} className={`question-item ${q.enabled === false ? 'question-disabled' : ''}`}>
                    <img src={q.image_filename} alt={`Q${i + 1}`} className="q-img clickable-img" onClick={() => setEnlargedImage(q.image_filename)} />
                    <div className="q-details">
                      {editingQuestion === q.id ? (
                        <div className="edit-form">
                          <div className="edit-options-grid">
                            {['a', 'b', 'c', 'd', 'e'].map((opt) => (
                              <div key={opt} className={`edit-field ${opt === 'e' ? 'edit-option-wide' : ''}`}>
                                <label>Option {opt.toUpperCase()}</label>
                                <input
                                  value={editForm[`option_${opt}`] || ''}
                                  onChange={(e) =>
                                    setEditForm({ ...editForm, [`option_${opt}`]: e.target.value })
                                  }
                                />
                              </div>
                            ))}
                          </div>
                          <div className="edit-secondary-grid">
                            <div className="edit-field">
                              <label>Correct Option</label>
                              <select
                                value={editForm.correct_option || ''}
                                onChange={(e) =>
                                  setEditForm({ ...editForm, correct_option: e.target.value })
                                }
                              >
                                {['A', 'B', 'C', 'D', 'E'].map((o) => (
                                  <option key={o} value={o}>{o}</option>
                                ))}
                              </select>
                            </div>
                            <div className="edit-field">
                              <label>Difficulty (1-10)</label>
                              <input
                                type="number"
                                min="1"
                                max="10"
                                value={editForm.difficulty || ''}
                                onChange={(e) =>
                                  setEditForm({ ...editForm, difficulty: e.target.value })
                                }
                                placeholder="unset"
                              />
                            </div>
                            <div className="edit-field">
                              <label>Time Limit (s)</label>
                              <input
                                type="number"
                                min="0"
                                value={editForm.time_limit_seconds || ''}
                                onChange={(e) =>
                                  setEditForm({ ...editForm, time_limit_seconds: e.target.value })
                                }
                                placeholder="unlimited"
                              />
                            </div>
                            <div className="edit-field edit-field-wide">
                              <label>Question prompt (optional)</label>
                              <textarea
                                className="edit-prompt"
                                value={editForm.question_text || ''}
                                onChange={(e) =>
                                  setEditForm({ ...editForm, question_text: e.target.value })
                                }
                                placeholder="Text shown under the image (leave blank for none)"
                              />
                            </div>
                            <div className="edit-field edit-field-wide">
                              <label>Video URL (YouTube)</label>
                              <input
                                value={editForm.video_url || ''}
                                onChange={(e) =>
                                  setEditForm({ ...editForm, video_url: e.target.value })
                                }
                                placeholder="https://youtube.com/watch?v=..."
                              />
                            </div>
                          </div>
                          <div className="edit-actions">
                            <button className="save-btn" onClick={() => saveEdit(q.id)}>Save</button>
                            <button className="cancel-btn" onClick={() => setEditingQuestion(null)}>Cancel</button>
                          </div>
                        </div>
                      ) : (
                        <div>
                          {q.question_text
                            ? <p className="q-prompt">{q.question_text}</p>
                            : <p className="q-no-prompt">No prompt set</p>}
                          <div className="q-options">
                            {['a', 'b', 'c', 'd', 'e'].map((opt) => (
                              <span
                                key={opt}
                                className={`q-option ${q.correct_option === opt.toUpperCase() ? 'correct' : ''}`}
                              >
                                {opt.toUpperCase()}: {q[`option_${opt}`]}
                              </span>
                            ))}
                          </div>
                          {q.video_url && (
                            <span className="q-has-video">▶ Has video</span>
                          )}
                          {q.time_limit_seconds > 0 && (
                            <span className="q-time-limit">⏱ {q.time_limit_seconds}s limit</span>
                          )}
                          {q.difficulty > 0 && (
                            <span className="q-difficulty">Difficulty {q.difficulty}/10</span>
                          )}
                          {q.enabled === false && (
                            <span className="q-disabled-badge">Disabled (hidden from quizzes)</span>
                          )}
                          <div className="q-topics">
                            {(q.topics || []).map((t) => (
                              <span key={t.id} className="q-topic-tag">{t.name}</span>
                            ))}
                            <select
                              className="topic-assign-select"
                              onChange={(e) => {
                                if (!e.target.value) return;
                                const topicId = parseInt(e.target.value);
                                const current = (q.topics || []).map(t => t.id);
                                if (!current.includes(topicId)) {
                                  saveTopics(q.id, [...current, topicId]);
                                }
                                e.target.value = '';
                              }}
                              defaultValue=""
                            >
                              <option value="">+ Add topic</option>
                              {topics.filter(t => !(q.topics || []).find(qt => qt.id === t.id)).map((t) => (
                                <option key={t.id} value={t.id}>{t.name}</option>
                              ))}
                            </select>
                            {(q.topics || []).map((t) => (
                              <button
                                key={t.id}
                                className="topic-remove-btn"
                                onClick={() => saveTopics(q.id, (q.topics || []).filter(qt => qt.id !== t.id).map(qt => qt.id))}
                              >
                                {t.name} ✕
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                    {editingQuestion !== q.id && (
                      <div className="q-actions">
                        <button className="edit-btn" onClick={() => startEdit(q)}>Edit</button>
                        <button
                          className={q.enabled === false ? 'enable-btn' : 'disable-btn'}
                          onClick={() => toggleEnabled(q.id, q.enabled === false)}
                        >
                          {q.enabled === false ? 'Enable' : 'Disable'}
                        </button>
                        <button className="delete-btn" onClick={() => deleteQuestion(q.id)}>Delete</button>
                      </div>
                    )}
                  </div>
                      );
                    })}
                </div>

                {questions.length === 0 && (
                  <p className="empty-msg">No questions found.</p>
                )}

                {questionTotalPages > 1 && (
                  <div className="pagination">
                    <button
                      className="page-btn"
                      onClick={() => setQuestionPage((p) => Math.max(1, p - 1))}
                      disabled={questionPage === 1}
                    >
                      ← Prev
                    </button>
                    <span className="page-info">Page {questionPage} of {questionTotalPages}</span>
                    <button
                      className="page-btn"
                      onClick={() => setQuestionPage((p) => Math.min(questionTotalPages, p + 1))}
                      disabled={questionPage === questionTotalPages}
                    >
                      Next →
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Upload Tab */}
        {tab === 'upload' && (
          <InstructorPage embedded />
        )}
      </div>
    </div>
  );
}
